import React, { Component } from "react";
import { Button, Checkbox, DatePicker, Select, AutoComplete, Tag } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import moment from "moment";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ALERT_TYPES = [
  { label: "SOC", value: "LOW SOC" },
  { label: "Voltage", value: "HIGH VOLTAGE" },
  { label: "Bat Temp", value: "HIGH BATTERY TEMPERATURE" },
  { label: "Motor", value: "HIGH MOTOR TEMPERATURE" }
];

const THRESHOLD_BADGES = [
  { label: "SOC ≤ 30%" },
  { label: "Voltage > 630V" },
  { label: "Bat Temp > 42C" },
  { label: "Motor > 90C" }
];

class EvAlertSearch extends Component {
  state = {
    isCreated: true,
    isInprogress: true,
    isClosed: false,
    fromDate: new Date().setHours(0, 0, 0, 0),
    toDate: new Date().setHours(23, 59, 59, 999),
    busRc: "",
    alertType: undefined,
    closureType: undefined,
    route: undefined,
    routeId: undefined
  };

  buildStatuses = () => {
    const { isCreated, isInprogress, isClosed } = this.state;
    const statuses = [];
    if (isCreated) statuses.push("CREATED");
    if (isInprogress) statuses.push("INPROGRESS");
    if (isClosed) statuses.push("CLOSED");
    return statuses.join(",");
  };

  // Search by BUS RC: only the RC within the selected time range.
  // Every other filter (status, alert type, resolution, route) is ignored.
  onSearchByRc = () => {
    const { fromDate, toDate, busRc } = this.state;
    this.props.onSearch({
      statuses: "CREATED,INPROGRESS,CLOSED", // don't constrain by status
      from: fromDate,
      to: toDate,
      busRc: busRc || undefined
    });
  };

  // Search / Refresh: apply ALL currently selected filters.
  onSearchAll = () => {
    const {
      fromDate,
      toDate,
      busRc,
      alertType,
      closureType,
      routeId
    } = this.state;
    this.props.onSearch({
      statuses: this.buildStatuses(),
      from: fromDate,
      to: toDate,
      busRc: busRc || undefined,
      alertType: alertType ? alertType.join(",") : undefined,
      closureType,
      route: routeId || undefined // service _id
    });
  };

  render() {
    const {
      isCreated,
      isInprogress,
      isClosed,
      fromDate,
      toDate,
      busRc,
      alertType,
      closureType,
      route
    } = this.state;
    const { busOptions = [], serviceOptions = [] } = this.props;

    const dateValue =
      fromDate && toDate ? [moment(fromDate), moment(toDate)] : null;

    return (
      <div style={{ padding: "16px 0 4px" }}>
        {/* Row 1: Title + all filters */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 10
          }}
        >
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#262626",
              marginRight: 8,
              whiteSpace: "nowrap"
            }}
          >
            Alerts
          </span>

          <AutoComplete
            placeholder="Search by BUS RC"
            style={{ width: 170 }}
            value={busRc}
            options={busOptions.map(rc => ({ value: rc }))}
            filterOption={(input, option) =>
              option.value.toUpperCase().includes((input || "").toUpperCase())
            }
            onChange={v => this.setState({ busRc: v })}
            onKeyDown={e => {
              if (e.key === "Enter") this.onSearchByRc();
            }}
            allowClear
          />
          <Button
            type="primary"
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
            onClick={this.onSearchByRc}
          >
            Search
          </Button>

          <Select
            allowClear
            mode="multiple"
            placeholder="All Alert Types"
            style={{ minWidth: 160 }}
            value={alertType}
            onChange={v => this.setState({ alertType: v })}
            suffixIcon={<CaretDownOutlined />}
          >
            {ALERT_TYPES.map(a => (
              <Option key={a.value} value={a.value}>
                {a.label}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            placeholder="All Resolution"
            style={{ width: 150 }}
            value={closureType}
            onChange={v => this.setState({ closureType: v })}
            suffixIcon={<CaretDownOutlined />}
          >
            <Option value="AUTO">Auto</Option>
            <Option value="MANUAL">Manual</Option>
          </Select>

          <AutoComplete
            placeholder="Choose Route"
            style={{ width: 300 }}
            value={route}
            options={serviceOptions.map(s => ({ value: s.name, id: s._id }))}
            filterOption={(input, option) =>
              option.value.toLowerCase().includes((input || "").toLowerCase())
            }
            onChange={v => {
              // Display the name, but remember the matching service _id.
              const match = serviceOptions.find(s => s.name === v);
              this.setState({
                route: v,
                routeId: match ? match._id : undefined
              });
            }}
            allowClear
          />

          <RangePicker
            value={dateValue}
            onChange={dates => {
              if (dates && dates.length === 2) {
                this.setState({
                  fromDate: dates[0].startOf("day").valueOf(),
                  toDate: dates[1].endOf("day").valueOf()
                });
              } else {
                this.setState({ fromDate: null, toDate: null });
              }
            }}
            format="D MMM YYYY"
            allowClear={false}
          />

          <div style={{ marginLeft: "auto" }}>
            <Button
              style={{
                background: "#ede9fe",
                border: "none",
                color: "#7c3aed",
                fontWeight: 500,
                boxShadow: "none"
              }}
              onClick={this.onSearchAll}
            >
              Search / Refresh
            </Button>
          </div>
        </div>

        {/* Row 2: Status checkboxes + Threshold badges */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <Checkbox
              checked={isClosed}
              onChange={e => this.setState({ isClosed: e.target.checked })}
            >
              CLOSED
            </Checkbox>
            <Checkbox
              checked={isInprogress}
              onChange={e => this.setState({ isInprogress: e.target.checked })}
            >
              INPROGRESS
            </Checkbox>
            <Checkbox
              checked={isCreated}
              onChange={e => this.setState({ isCreated: e.target.checked })}
            >
              CREATED
            </Checkbox>
          </div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span style={{ color: "#8c8c8c", fontSize: 13, fontWeight: 500 }}>
              Thresholds:
            </span>
            {THRESHOLD_BADGES.map(b => (
              <Tag
                key={b.label}
                style={{
                  background: "#fff7e6",
                  border: "1px solid #ffa940",
                  color: "#d46b08",
                  borderRadius: 4,
                  fontWeight: 500,
                  fontSize: 12,
                  margin: 0
                }}
              >
                {b.label}
              </Tag>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

export default EvAlertSearch;
